import { useEffect, useState } from "react";
import api, { apiError } from "../../lib/api";
import { ShoppingCart, RefreshCw, Mail } from "lucide-react";
import { toast } from "sonner";

const fmt = (n) => `₹${(n || 0).toLocaleString("en-IN")}`;

export default function AdminAbandonedCarts() {
  const [carts, setCarts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/abandoned-carts");
      setCarts(data);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const open = carts.filter((c) => c.status === "open").length;

  return (
    <div className="p-6 md:p-10" data-testid="admin-abandoned-carts">
      <header className="mb-8 flex items-end justify-between flex-wrap gap-4">
        <div>
          <span className="font-script text-3xl text-blush-400">so close</span>
          <h1 className="font-heading text-3xl md:text-4xl text-ink-900">Abandoned checkouts</h1>
          <p className="mt-2 font-body text-ink-500 text-sm">
            Shoppers who entered their details at checkout but didn't complete the purchase.
            {" "}{open} still open · a recovery email is sent automatically once, ~1 hour after they leave.
          </p>
        </div>
        <button
          onClick={load}
          disabled={loading}
          data-testid="abandoned-carts-refresh"
          className="btn-ghost-rose text-xs inline-flex items-center gap-2 disabled:opacity-60"
        >
          <RefreshCw size={12} strokeWidth={1.6} className={loading ? "animate-spin" : ""} />
          Refresh
        </button>
      </header>

      <div className="rounded-3xl bg-white border border-blush-100 overflow-hidden">
        {loading ? (
          <div className="p-10 text-center text-ink-500 text-sm">Loading…</div>
        ) : carts.length === 0 ? (
          <div className="p-16 text-center text-ink-500 font-body">
            <ShoppingCart size={32} strokeWidth={1.3} className="mx-auto text-blush-400 mb-3" />
            No abandoned checkouts yet.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-blush-50/60 text-[11px] uppercase tracking-[0.16em] text-ink-500">
                <tr>
                  <th className="px-4 py-3 text-left">Email</th>
                  <th className="px-4 py-3 text-left">Items</th>
                  <th className="px-4 py-3 text-left">Value</th>
                  <th className="px-4 py-3 text-left">Status</th>
                  <th className="px-4 py-3 text-left">Recovery email</th>
                  <th className="px-4 py-3 text-left">Left at</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-blush-100">
                {carts.map((c) => (
                  <>
                    <tr
                      key={c.id}
                      onClick={() => setExpanded(expanded === c.id ? null : c.id)}
                      data-testid={`abandoned-cart-row-${c.id}`}
                      className="cursor-pointer hover:bg-blush-50/40 transition"
                    >
                      <td className="px-4 py-3 text-ink-900">
                        <a href={`mailto:${c.email}`} onClick={(e) => e.stopPropagation()} className="hover:text-blush-600 hover:underline inline-flex items-center gap-1">
                          <Mail size={12} strokeWidth={1.6} /> {c.email}
                        </a>
                      </td>
                      <td className="px-4 py-3 text-ink-700">{(c.items || []).length} item{(c.items || []).length === 1 ? "" : "s"}</td>
                      <td className="px-4 py-3 text-ink-900">{fmt(c.total)}</td>
                      <td className="px-4 py-3">
                        <span className={`text-xs uppercase tracking-[0.14em] px-2 py-1 rounded-full ${
                          c.status === "open" ? "bg-amber-50 text-amber-700" : c.status === "recovered" ? "bg-emerald-50 text-emerald-700" : "bg-blush-50 text-blush-700"
                        }`}>
                          {c.status}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-ink-700">{c.email_sent ? "Sent" : "Not sent yet"}</td>
                      <td className="px-4 py-3 text-ink-500 text-xs">{(c.created_at || "").slice(0, 16).replace("T", " · ")}</td>
                    </tr>
                    {expanded === c.id && (
                      <tr>
                        <td colSpan={6} className="px-4 pb-4 bg-blush-50/20">
                          <ul className="text-xs font-body text-ink-700 space-y-1 pt-2">
                            {(c.items || []).map((i, idx) => (
                              <li key={idx} className="flex justify-between max-w-md">
                                <span>{i.name || i.product_id} {i.size ? `· ${i.size}` : ""} × {i.quantity || 1}</span>
                                <span>{fmt((i.price || 0) * (i.quantity || 1))}</span>
                              </li>
                            ))}
                          </ul>
                        </td>
                      </tr>
                    )}
                  </>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
